/**
 * FilingReview — SLIP+ Filing Review Screen (A4)
 *
 * Route: /FilingQueue/Review/:state/:customerPolicyId   (state = "Tennessee" | "Florida")
 *
 * Opened per-row from the Filing Queue, which is CustomerPolicyId-keyed. Calls the file-mule
 * INTERNAL API where the per-state SLIP+ filing module lives, under /api/AutomaticFiling/<State>:
 *   GET  /api/AutomaticFiling/<State>/review/policy/:customerPolicyId
 *   POST /api/AutomaticFiling/<State>/build-batch     body: { customerPolicyId, overrides? }
 *
 * The CustomerPolicyId form mirrors the Florida path (Florida is CustomerPolicyId-keyed); the
 * backend resolves it to the policy's filing transaction (OpenTaxReportId) server-side. The screen
 * renders generically off the { fields, readiness, report } payload, so it serves any state whose
 * backend exposes that review shape. Tennessee is live; Florida works once its review/policy
 * endpoint returns the same shape.
 *
 * `config` (Constants.js) is the internal-API base — the same origin the rest of the app uses. Raw
 * fetch is kept here (rather than the apiClient wrapper) only so the response envelope is read as-is;
 * the bearer token is the standard one.
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useExtractionStates } from "../helper/filingReviewStates";
import { Alert, Box, Button, Card, CardContent,
    Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Divider, FormControl, FormHelperText,
    Grid, InputLabel, LinearProgress, MenuItem, Select,
    SelectChangeEvent, Stack, TextField, Typography,
} from "@mui/material";
import { ArrowBack, Build, CheckCircleOutline, Download, ErrorOutline, WarningAmberOutlined } from "@mui/icons-material";
import Swal from "sweetalert2";
import { truncationWarningHtml } from "../helper/truncationWarning";
import useAuth from "../context/AuthProvider";
import { config } from "../Constants";
import { AppRoutes } from "../helper/appRoutes";
import { buttonColors, GreenBtnColor } from "../helper/theme";
import { DocumentViewer } from "../components/Elements/DocumentViewer";
import { TAX_STATUS_OPTIONS, isLloydsInsurer, LLOYDS_UMR_LABEL, ADDRESS_COMPONENT_KEYS, LEGACY_ADDRESS_KEYS } from "../helper/filingFieldOptions";

// Internal-API base for the SLIP+ filing modules. `config` already ends in `/api`, so this resolves
// to e.g. http://localhost:8080/api/AutomaticFiling/Tennessee in dev. The state segment comes from
// the route param; the queue passes the row's filingState.
const filingBaseFor = (state: string) => `${config}/AutomaticFiling/${state}`;

// stateSegment maps a filingState value (full name or 2-letter) to the backend module segment.
// The supported-state list lives in the API (GET /api/AutomaticFiling/getExtractionStates) and is
// fetched once per session via useExtractionStates — add new states on the backend only.

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FieldGroup = "document" | "policyRecord" | "computed" | "filer";
type FieldType = "currency" | "date" | string;

interface FilingField {
    key: string;
    label: string;
    group: FieldGroup;
    value: string | number | null;
    detail?: string;
    source?: string;
    editable: boolean;
    type?: FieldType;
    // Tier 2 provenance (present when the extraction persisted it; null = Slice A / values-only).
    confidence?: number | null;
    sourcePage?: number | null;
    sourceQuote?: string | null;
    /** Which document this field was extracted from (e.g. "Declination page"); null until the
        extraction records it per field. Shown in the source chip when present. */
    sourceDocument?: string | null;
    provenanceStatus?: "with_provenance" | "values_only" | string;
    required?: boolean;
};

interface ReadinessGap {
    key: string;
    label: string;
    note: string;
}

interface Readiness {
    grade: number; // 0..1
    requiredReady: number;
    requiredTotal: number;
    gaps: ReadinessGap[];
}

interface ManualReviewFlag {
    policyNumber: string;
    transactionId?: string;
    kind:
    | "lloyds_syndicate_confirm"
    | "declinations_missing"
    | "declination_naic_missing"
    | "declinations_short"
    | string;
    reason: string;
}

interface FilingReport {
    errors: string[];
    warnings: string[];
    manualReview: ManualReviewFlag[];
    readiness?: Readiness;
}

interface ReviewData {
    ready: boolean;
    fields: FilingField[];
    batch?: unknown;
    readiness?: Readiness;
    report: FilingReport;
    /** The dec/policy PDF the per-field page-links open at #page=N (Tier 2). */
    sourceDocumentUrl?: string | null;
    /** ISO timestamp of when the latest extraction for this policy ran; null if never extracted.
        Shown on the review header so the reviewer knows how fresh the pulled data is (QA 2026-07-07). */
    extractedAt?: string | null;
}

interface BuildResult {
    fileName: string;
    sizeBytes: number;
    xml: string;
    report: FilingReport;
}

// ---------------------------------------------------------------------------
// Design tokens (matching LicenseMatrix.tsx / house style)
// ---------------------------------------------------------------------------
const BLUE = "#2099d2";
const BLUE_HOVER = "#176990";
const DARK_SIDEBAR = "#132D3D";
const SLATE_700 = "#334155";
const SLATE_500 = "#475569";
const SLATE_400 = "#64748b";
const BORDER = "#e2e8f0";
const CARD_SHADOW =
    "0 4px 24px rgba(15,23,42,0.08), 0 1px 3px rgba(15,23,42,0.06)";
const SECTION_BG = "#eef2fb";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Section headers. Plain-language for non-technical filers (Jerry, 2026-06-25 walkthrough):
// "Policy Record" reads as a raw DB term, "Filing Entity" hid that it's the licensee/client.
const GROUP_LABELS: Record<string, string> = {
    document: "Extracted Document Fields (Editable)",
    policyRecord: "MISC Database",
    computed: "Computed Values",
    filer: "Licensee",
};

// Per-field source chip labels — the small grey "where this came from" tag under each field. The raw
// group keys (document / policyRecord / computed / filer) are internal; show friendly labels instead.
const SOURCE_LABELS: Record<string, string> = {
    document: "Extracted Document",
    policyRecord: "MISC Database",
    computed: "Computed",
    filer: "Licensee",
};

/** Friendly per-field source chip text: the relabeled source group, plus the specific source
 *  document when the extraction recorded it ("Extracted Document · Declination page"); else detail. */
const sourceText = (field: FilingField): string | null => {
    const base = field.source ? (SOURCE_LABELS[field.source] ?? field.source) : null;
    if (base && field.sourceDocument) return `${base} · ${field.sourceDocument}`;
    return base ?? field.detail ?? null;
};

const GROUP_ORDER: FieldGroup[] = ["document", "policyRecord", "computed", "filer"];

function groupFields(fields: FilingField[]): Record<string, FilingField[]> {
    const grouped: Record<string, FilingField[]> = {};
    for (const g of GROUP_ORDER) grouped[g] = [];
    for (const f of fields) {
        if (!grouped[f.group]) grouped[f.group] = [];
        grouped[f.group].push(f);
    }
    return grouped;
}

function pct(grade: number): string {
    return `${Math.round(grade * 100)}%`;
}

function formatCurrency(value: string | number): string {
    const num = typeof value === "number" ? value : parseFloat(String(value).replace(/[^0-9.-]/g, ""));
    if (isNaN(num)) return String(value);
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(num);
};

function formatDate(value: string | number): string {
    const raw = String(value).trim();
    if (!raw) return raw;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const yyyy = d.getUTCFullYear();
    return `${mm}-${dd}-${yyyy}`;
};

/** Human date+time for the "Last extracted …" header caption (local tz). Falls back to the raw
    string if unparseable. Unlike formatDate this keeps the time, which is what reviewers asked for. */
function formatDateTime(value: string): string {
    const raw = String(value).trim();
    if (!raw) return raw;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleString("en-US", {
        year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
};

function formatFieldValue(field: FilingField): string {
    if (field.value === null || field.value === undefined || field.value === "") return "";
    if (field.type === "date") return formatDate(field.value);
    if (field.type === "currency") return formatCurrency(field.value);
    return String(field.value);
};

function kindLabel(kind: string): string {
    switch (kind) {
        case "lloyds_syndicate_confirm":
            return "Lloyd's Syndicate NAIC needed";
        case "declinations_missing":
            return "Declinations missing";
        case "declination_naic_missing":
            return "Declination carrier NAIC missing";
        case "declinations_short":
            return "Not enough declinations";
        default:
            return kind;
    }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SectionHeader({ title, hint }: { title: string; hint?: string; }) {
    return (
        <Box
            sx={{
                backgroundColor: SECTION_BG,
                px: 2.5,
                py: 1.75,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: `1px solid ${BORDER}`,
            }}
        >
            <Typography
                sx={{
                    fontWeight: 700,
                    fontSize: "15px",
                    color: SLATE_700,
                }}
            >
                {title}
            </Typography>
            {hint && (
                <Typography sx={{ fontSize: "12px", color: SLATE_400 }}>
                    {hint}
                </Typography>
            )}
        </Box>
    );
}

function ReadOnlyField({ field, isGap }: { field: FilingField; isGap?: boolean }) {
    const isEmpty = field.value === null || field.value === "" || field.value === undefined;
    const valueColor = isGap ? "#c0392b" : isEmpty ? "#aab4bb" : SLATE_700;
    return (
        <Box>
            <Typography sx={{ fontSize: "12px", color: isGap ? "#c0392b" : SLATE_400, mb: 0.5, fontWeight: isGap ? 700 : 400 }}>
                {field.label}{field.required ? <span style={{ color: "#c0392b" }}> *</span> : ""}
            </Typography>
            <Typography
                sx={{
                    fontSize: "15px",
                    fontWeight: 600,
                    color: valueColor,
                    wordBreak: "break-word",
                }}
            >
                {isEmpty ? "—" : formatFieldValue(field)}
            </Typography>
            {(sourceText(field)) && (
                <Typography sx={{ fontSize: "11.5px", color: SLATE_400, mt: 0.5 }}>
                    {sourceText(field)}
                </Typography>
            )}
        </Box>
    );
}

// Confidence → chip color/label. >=90% green, >=70% amber, else red.
function confidenceColor(c: number): { bg: string; fg: string } {
    if (c >= 0.9) return { bg: "#e6f4ea", fg: "#1e7e34" };
    if (c >= 0.7) return { bg: "#fff4e0", fg: "#b26a00" };
    return { bg: "#fdecea", fg: "#c0392b" };
}

interface FieldProvenanceProps {
    field: FilingField;
    docUrl?: string | null;
    onViewDocument?: (url: string, name: string, page?: number | null) => void;
};

/** Tier 2 provenance row shown under a field: a derived-confidence chip, a "view page N" deep-link
 * into the source PDF (opens at #page=N), and the capped quote the value came from. Renders nothing
 * for Slice A fields (no confidence and no page) so values-only policies look exactly as before.
 */
function FieldProvenance({ field, docUrl, onViewDocument }: FieldProvenanceProps) {
    // An empty field has no meaningful provenance — never show a confidence chip or page-link on a
    // blank value (QA 2026-07-07: a 100%-confidence chip rendered on an empty Insurer Eligibility field).
    const isEmpty = field.value === null || field.value === undefined || String(field.value).trim() === "";
    if (isEmpty) return null;

    const hasConfidence = typeof field.confidence === "number";
    const hasPage = field.sourcePage != null;
    if (!hasConfidence && !hasPage && !field.sourceQuote) return null;

    const confidencePct = hasConfidence ? pct(field.confidence as number) : null;
    const col = hasConfidence ? confidenceColor(field.confidence as number) : null;

    return (
        <Box sx={{ mt: 0.75, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}>
            {confidencePct && col && (
                <Box title="Extraction confidence (derived from where the value was found)"
                    sx={{
                        fontSize: "10.5px",
                        fontWeight: 700,
                        color: col.fg,
                        backgroundColor: col.bg,
                        borderRadius: "4px",
                        px: 0.75,
                        py: "1px",
                        lineHeight: 1.6,
                    }}
                >{confidencePct} confidence</Box>
            )}
            {hasPage && docUrl && (
                <Box component="button" title="Open the source document at this page"
                    onClick={() => {
                        const raw = docUrl.split("/").pop()?.split("?")[0] ?? "Policy Document";
                        const name = decodeURIComponent(raw);
                        onViewDocument?.(docUrl, name, field.sourcePage);
                    }}
                    sx={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: BLUE,
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                    }}
                >source p.{field.sourcePage} ↗</Box>
            )}
            {hasPage && !docUrl && (
                <Box sx={{ fontSize: "11px", color: SLATE_400 }}>source p.{field.sourcePage}</Box>
            )}
            {field.sourceQuote && (
                <Typography title={field.sourceQuote}
                    sx={{
                        fontSize: "11px",
                        fontStyle: "italic",
                        color: SLATE_400,
                        width: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >“{field.sourceQuote}”</Typography>
            )}
        </Box>
    );
};


const ELIGIBILITY_STATUS_OPTIONS = [
    "Listed alien insurer",
    "Approved non-admitted insurer",
    "Eligible surplus lines insurer",
    "Other",
].map((opt) => ({ label: opt, value: opt }));

const ENTITY_TYPE_OPTIONS = [
    "LLC",
    "Corporation",
    "Partnership",
    "Sole Proprietor",
    "Individual",
    "Trust",
    "Other",
].map((opt) => ({ label: opt, value: opt }));

const EXEMPT_COMMERCIAL_PURCHASER_OPTIONS = [
    { label: "Yes", value: "true" },
    { label: "No", value: "false" },
];

interface DropdownOption { label: string; value: string; };

interface EditableFieldProps {
    field: FilingField;
    value: string;
    onChange: (key: string, val: string) => void;
    isLloydsNaicField?: boolean;
};

function EditableField({ field, value, onChange, isLloydsNaicField }: EditableFieldProps) {
    const needsReview = isLloydsNaicField || (field.key === "writingInsurerNaicDoc" && !value);

    const DropdownComponent = ({ options }: { options: DropdownOption[] }) => (
        <FormControl fullWidth size="small">
            <InputLabel sx={{ fontSize: "13px" }} id={`label-${field.key}`}>
                {field.label}
            </InputLabel>
            <Select labelId={`label-${field.key}`}
                value={value}
                label={field.label}
                onChange={(e: SelectChangeEvent<string>) => onChange(field.key, e.target.value)}
                sx={{ fontSize: "14px" }}
            >
                {options.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "14px" }}>
                        {opt.label}
                    </MenuItem>
                ))}
            </Select>
            {(sourceText(field)) && (
                <FormHelperText sx={{ fontSize: "11px", color: SLATE_400 }}>
                    {sourceText(field)}
                </FormHelperText>
            )}
        </FormControl>
    );

    if(field.key === "eligibilityStatus") {
        return <DropdownComponent options={ELIGIBILITY_STATUS_OPTIONS} />;
    }
    if(field.key === "entityType") {
        return <DropdownComponent options={ENTITY_TYPE_OPTIONS} />;
    }
    if(field.key === "exemptCommercialPurchaser") {
        return <DropdownComponent options={EXEMPT_COMMERCIAL_PURCHASER_OPTIONS} />;
    }
    if(field.key === "taxStatus") {
        return <DropdownComponent options={TAX_STATUS_OPTIONS} />;
    }

    if (field.key === "declinationCount") {
        return (
            <TextField
                fullWidth
                size="small"
                label={field.label}
                type="number"
                value={value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(field.key, e.target.value)}
                helperText={sourceText(field)}
                FormHelperTextProps={{ sx: { fontSize: "11px", color: SLATE_400 } }}
                InputProps={{ inputProps: { min: 0 } }}
                sx={{ "& .MuiInputBase-input": { fontSize: "14px" } }}
            />
        );
    }

    if (field.key === "descriptionOfRisk") {
        return (
            <TextField
                fullWidth
                size="small"
                label={field.label}
                multiline
                minRows={2}
                value={value}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(field.key, e.target.value)}
                helperText={sourceText(field)}
                FormHelperTextProps={{ sx: { fontSize: "11px", color: SLATE_400 } }}
                sx={{ "& .MuiInputBase-input": { fontSize: "14px" } }}
            />
        );
    }

    // writingInsurerNaicDoc gets highlighted styling when it needs a value
    const isNaicDoc = field.key === "writingInsurerNaicDoc";
    return (
        <TextField
            fullWidth
            size="small"
            label={field.label}
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(field.key, e.target.value)}
            placeholder={
                isNaicDoc && !value
                    ? "Enter syndicate-specific NAIC (e.g. AA1122000)"
                    : undefined
            }
            helperText={sourceText(field)}
            FormHelperTextProps={{ sx: { fontSize: "11px", color: SLATE_400 } }}
            sx={{
                "& .MuiInputBase-input": { fontSize: "14px" },
                ...(needsReview && !value
                    ? {
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e6c78c",
                        },
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: "#fffaf2",
                        },
                    }
                    : {}),
            }}
        />
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

// Triggers a browser download of the built SLIP+ batch. Module-scope so both the
// auto-download on a successful File and the manual "Download XML" button share one path.
function downloadXmlFile(xml: string, fileName: string) {
    const blob = new Blob([xml], { type: "application/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
}

export const FilingReview: React.FC = () => {
    const { user } = useAuth();
    const token = user?.token;
    const { state: stateParam, customerPolicyId } = useParams<{
        state: string;
        customerPolicyId: string;
    }>();
    const navigate = useNavigate();
    const { stateSegment } = useExtractionStates();

    // Resolve the route's state param to a backend module segment (default Tennessee for
    // backward-compatible links that omit the state).
    const segment = stateSegment(stateParam) ?? "Tennessee";
    const FILING_BASE = filingBaseFor(segment);
    const stateUnsupported = stateParam != null && stateSegment(stateParam) === null;
    // 2-letter state for the generic extraction trigger (POST /api/AutomaticFiling/extract).
    const stateCode = (({ Tennessee: "TN", Florida: "FL", California: "CA" } as Record<string, string>)[segment]) ?? "TN";

    const [loading, setLoading] = useState(true);
    const [buildLoading, setBuildLoading] = useState(false);
    const [extractLoading, setExtractLoading] = useState(false);
    const [reviewData, setReviewData] = useState<ReviewData | null>(null);
    const [fetchError, setFetchError] = useState<string | null>(null);


    // Overrides: only keys the reviewer actually changed
    const [overrides, setOverrides] = useState<Record<string, string>>({});
    // Original document-field values for diff
    const [originalDocValues, setOriginalDocValues] = useState<
        Record<string, string>
    >({});
    // Current editable values (original + any overrides)
    const [editValues, setEditValues] = useState<Record<string, string>>({});

    // XML preview modal
    const [xmlModalOpen, setXmlModalOpen] = useState(false);
    const [buildResult, setBuildResult] = useState<BuildResult | null>(null);
    const [buildErrors, setBuildErrors] = useState<string[]>([]);
    const [buildWarnings, setBuildWarnings] = useState<string[]>([]);
    // Scroll anchor for the inline build-error/blockers panel.
    const buildErrorsRef = useRef<HTMLDivElement>(null);

    // Surface a failed build LOUDLY: set the inline list, pop a modal, AND auto-scroll to the panel.
    // A validation rejection must never look like a silent/broken no-op — the engine validated fine,
    // the errors were just off-screen with no signal (Jerry, 2026-06-25 walkthrough).
    const surfaceBuildErrors = useCallback((errors: string[], warnings: string[] = []) => {
        const errs = errors.length ? errors : ["Build failed — no detail returned."];
        setBuildErrors(errs);
        setBuildWarnings(warnings);
        const esc = (s: string) => String(s).replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c] as string));
        const toBlockers = () => buildErrorsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        Swal.fire({
            title: errs.length === 1 ? "1 filing blocker" : `${errs.length} filing blockers`,
            html: `<div style="text-align:left;font-size:14px">Resolve these before this can be filed:<ul style="margin:8px 0 0 18px">${errs.map((e) => `<li style="margin-bottom:4px">${esc(e)}</li>`).join("")}</ul></div>`,
            icon: "error",
            confirmButtonText: "Review blockers",
            confirmButtonColor: BLUE,
        }).then(toBlockers);
        // Also scroll if the modal is dismissed via backdrop/Escape.
        window.setTimeout(toBlockers, 60);
    }, []);

    // Policy document viewer
    const [docViewerOpen, setDocViewerOpen] = useState(false);
    const [docViewerDocuments, setDocViewerDocuments] = useState<{ documentUrl: string; documentName: string; page?: number | null }[]>([]);

    // Monotonic counter for review loads, so a stale (superseded) response can't clobber newer state.
    const requestSeq = useRef(0);

    // ---------------------------------------------------------------------------
    // Fetch review data
    // ---------------------------------------------------------------------------
    // Apply a loaded review payload: store it and (re)seed the editable document-field state. Shared
    // by the initial policy load and the per-transaction switcher so the seeding logic lives in one
    // place. Caller layers on transaction-list / selected-OTR state via the loadReview onData hook.
    const applyReviewData = useCallback((data: ReviewData) => {
        setReviewData(data);
        // Seed edit state from the fields the backend marked editable (honor field.editable rather than
        // assuming "document group === editable" — a document field the backend locks must not be
        // editable, and an editable field outside the document group must still be seeded).
        const origVals: Record<string, string> = {};
        for (const f of data.fields ?? []) {
            if (f.editable) {
                origVals[f.key] = f.value !== null && f.value !== undefined ? String(f.value) : "";
            }
        }
        setOriginalDocValues(origVals);
        setEditValues({ ...origVals });
        setOverrides({});
        // Clear any prior build output so switching transactions never leaves the previous
        // transaction's XML / errors / warnings on screen for the next one.
        setBuildErrors([]);
        setBuildWarnings([]);
        setBuildResult(null);
        setXmlModalOpen(false);
    }, []);

    // The shared GET-and-unwrap for both review endpoints. Raw fetch (not apiClient) so the response
    // envelope is read as-is; the internal API already returns camelCase keys (Node backend). The API
    // can return a 200 with a failure envelope ({ status:false,... }), e.g. "No filing data found" —
    // treat that as an error rather than casting the envelope to ReviewData (mirrors build-batch).
    // `onData` runs after the data is applied, for endpoint-specific state (switcher list / OTR).
    const loadReview = useCallback(
        async (url: string, onData?: (data: ReviewData) => void) => {
            // Monotonic request id: a newer load (transaction switch, route change) supersedes this one.
            // We ignore every response that isn't the latest request so a slow earlier fetch can never
            // overwrite newer reviewData / selectedOtr / edit state (which would let a reviewer file
            // against stale fields). The check runs after each await point.
            const reqId = ++requestSeq.current;
            setLoading(true);
            setFetchError(null);
            try {
                const res = await fetch(url, {
                    method: "GET",
                    headers: new Headers({
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    }),
                });
                if (reqId !== requestSeq.current) return;
                if (!res.ok) {
                    let msg = `HTTP ${res.status}`;
                    try {
                        const body = await res.json();
                        msg = body?.message || msg;
                    } catch {}
                    if (reqId === requestSeq.current) setFetchError(msg);
                    return;
                }
                const json = (await res.json()) as any;
                if (reqId !== requestSeq.current) return;
                if (json?.status === false) {
                    setFetchError(json?.message || "No filing data returned for this filing.");
                    return;
                }
                const data: ReviewData = (json?.data ?? json) as ReviewData;
                // Shape guard: a 200 with a malformed body (no fields array) must not be cast to ReviewData
                // and rendered as an empty/partial filing — surface it as an error instead.
                if (!data || !Array.isArray(data.fields)) {
                    setFetchError("Filing review returned an unexpected response.");
                    return;
                }
                applyReviewData(data);
                onData?.(data);
            } catch (err: any) {
                if (reqId === requestSeq.current) setFetchError(err?.message || "Network error");
            } finally {
                if (reqId === requestSeq.current) setLoading(false);
            }
        },
        [token, applyReviewData]
    );

    const fetchReview = useCallback(async () => {
        if (!customerPolicyId || !token) return;
        if (stateUnsupported) {
            setFetchError(`Filing review is not available for "${stateParam}".`);
            setLoading(false);
            return;
        }
        await loadReview(`${FILING_BASE}/review/policy/${customerPolicyId}`);
    }, [customerPolicyId, token, FILING_BASE, stateUnsupported, stateParam, loadReview]);

    // Run a real extraction for this policy's document(s) via the state-generic engine trigger
    // (POST /api/AutomaticFiling/extract), then reload the review so the freshly-extracted data,
    // confidence chips, and page-proof show. This is the in-app "extract a document" action.
    const handleExtract = useCallback(async () => {
        if (!customerPolicyId || !token) {
            Swal.fire({
                icon: "warning",
                title: "Can't start extraction",
                text: "Missing policy id or session — reload the page and try again.",
            });
            return;
        }
        setExtractLoading(true);
        try {
            const res = await fetch(`${config}/AutomaticFiling/extract`, {
                method: "POST",
                headers: new Headers({ "Content-Type": "application/json", Authorization: `Bearer ${token}` }),
                body: JSON.stringify({ customerPolicyId, state: stateCode }),
            });
            const json = await res.json().catch(() => null);
            if (!res.ok || json?.status === false) {
                Swal.fire({ icon: "error", title: "Extraction failed", text: json?.message || `HTTP ${res.status}` });
                return;
            }
            const d = json?.data ?? json;
            // Surface WHAT the engine pulled from the document, so the run isn't a silent spinner.
            // Intentionally NO model/tier or per-run cost here — that's internal detail, not for the
            // reviewer (QA feedback 2026-07-07). A non-persisted result cues manual review instead.
            const ed = (d?.extractedData ?? {}) as Record<string, any>;
            const uv = (v: any): string =>
                v && typeof v === "object" ? String(v.value ?? "") : v != null ? String(v) : "";
            // Extracted values come from the customer's uploaded document and are rendered as raw HTML by
            // Swal (innerHTML), so escape them before interpolation — a crafted insured name / address
            // must not break the popup layout or execute script in the reviewer's session (repo rule:
            // all values reflected in HTML pass through an escape; Gemini review 2026-07-07).
            const esc = (s: string): string =>
                s.replace(/[&<>"']/g, (c) =>
                    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));
            const rows: [string, string][] = (
                [
                    ["Insured", uv(ed.namedInsured) || uv(ed.insuredName)],
                    ["Entity type", uv(ed.insuredBusinessType)],
                    ["Coverage code", uv(ed.slipPlusCoverageCode)],
                    ["Writing insurer", uv(ed.writingInsurerName)],
                    ["Premium", uv(ed.grossPremium)],
                    ["Mailing address", uv(ed.insuredMailingAddress)],
                ] as [string, string][]
            ).filter(([, v]) => v);
            const rowsHtml = rows
                .map(
                    ([k, v]) =>
                        `<tr><td style="color:#7a8a93;padding:3px 14px 3px 0;white-space:nowrap;vertical-align:top">${esc(k)}</td><td style="font-weight:600;color:#2F4550">${esc(v)}</td></tr>`
                )
                .join("");
            await Swal.fire({
                icon: d?.persisted ? "success" : "warning",
                title: d?.persisted ? "Extraction complete" : "Extracted — needs review",
                html:
                    `<div style="text-align:left;font-size:13.5px">` +
                    // Page-truncation banner FIRST (escaped): an oversized PDF was read only up to the
                    // model's 100-page limit, so the reviewer must verify before trusting the fields.
                    truncationWarningHtml(d) +
                    `<div style="color:#586F7C;margin-bottom:10px">${[d?.persisted ? "" : "Needs manual review", `${Object.keys(ed).length} fields read from the document`]
                        .filter(Boolean)
                        .join(" · ")}</div>` +
                    `<table style="border-collapse:collapse">${rowsHtml}</table>` +
                    // Footer must match reality: a persisted run refreshed the review; a non-persisted run
                    // was NOT saved, so reloading shows the PREVIOUS data — never claim it refreshed then
                    // (would mislead a reviewer into filing stale values; Gemini review 2026-07-07).
                    (d?.persisted
                        ? `<div style="color:#9aa7ae;margin-top:12px;font-size:12px">The review below has been refreshed with the full extraction, confidence chips, and page-proof.</div>`
                        : `<div style="color:#b26a00;margin-top:12px;font-size:12px">This run was not saved to the record, so the review below still shows the previous data. Correct the fields manually, or re-run the extraction.</div>`) +
                    `</div>`,
                confirmButtonText: "View review",
                confirmButtonColor: "#0d4a63",
            });
            await fetchReview();
        } catch (err: any) {
            Swal.fire({ icon: "error", title: "Extraction failed", text: err?.message || "Network error" });
        } finally {
            setExtractLoading(false);
        }
    }, [customerPolicyId, token, stateCode, fetchReview]);

    const handleViewPolicyDocument = useCallback(() => {
        const url = reviewData?.sourceDocumentUrl;
        if (!url) return;
        const raw = url.split("/").pop()?.split("?")[0] ?? "Policy Document";
        setDocViewerDocuments([{ documentUrl: url, documentName: decodeURIComponent(raw) }]);
        setDocViewerOpen(true);
    }, [reviewData]);

    const handleCloseDocViewer = useCallback(() => {
        setDocViewerOpen(false);
        setDocViewerDocuments([]);
    }, [docViewerDocuments]);

    useEffect(() => {
        fetchReview();
    }, [fetchReview]);

    // ---------------------------------------------------------------------------
    // Handle field edits
    // ---------------------------------------------------------------------------
    const handleFieldChange = useCallback(
        (key: string, val: string) => {
            // Only fields the backend marked editable are seeded into originalDocValues; reject anything
            // else so an override can never be staged for a non-editable (or unknown) field.
            if (!(key in originalDocValues)) return;
            setEditValues((prev) => ({ ...prev, [key]: val }));
            // Track override only if value differs from original
            if (val !== (originalDocValues[key] ?? "")) {
                setOverrides((prev) => ({ ...prev, [key]: val }));
            } else {
                setOverrides((prev) => {
                    const next = { ...prev };
                    delete next[key];
                    return next;
                });
            }
        },
        [originalDocValues]
    );

    // ---------------------------------------------------------------------------
    // Build XML
    // ---------------------------------------------------------------------------
    const handleBuild = useCallback(async () => {
        if (!customerPolicyId || !token) return;

        setBuildLoading(true);
        setBuildErrors([]);
        setBuildWarnings([]);

        try {
            // Filing is keyed on the CustomerPolicyId (the queue + Florida path). The backend resolves
            // it one-per-policy: getTnFilingForPolicy dedupes the policy's OpenTaxReports to the latest
            // transaction and pulls amounts from CustomerPolicyTaxesAndFees — so there is no per-OTR pick.
            const body: Record<string, unknown> = { customerPolicyId: Number(customerPolicyId) };
            // Only include overrides map if there are actual changes
            if (Object.keys(overrides).length > 0) {
                body.overrides = overrides;
            }

            const res = await fetch(`${FILING_BASE}/build-batch`, {
                method: "POST",
                headers: new Headers({
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                }),
                body: JSON.stringify(body),
            });

            const json = await res.json() as any;
            const data = (json?.data ?? json) as any;

            if (!res.ok || json?.status === false) {
                // 400 — surface report errors/warnings (inline + popup + scroll)
                const rpt: FilingReport = data?.report ?? {};
                surfaceBuildErrors(rpt.errors ?? [json?.message ?? `HTTP ${res.status}`], rpt.warnings ?? []);
                return;
            }

            // A 200 is not proof of a valid filing: a body with no XML, or one whose own report carries
            // errors, is a failed build — surface it instead of opening the preview as if it succeeded.
            const reportErrors: string[] = Array.isArray(data?.report?.errors) ? data.report.errors : [];
            if (!data?.xml || reportErrors.length > 0) {
                surfaceBuildErrors(reportErrors.length > 0 ? reportErrors : ["Build returned no XML."], data?.report?.warnings ?? []);
                return;
            }

            setBuildResult({
                fileName: data.fileName ?? "filing.xml",
                sizeBytes: data.sizeBytes ?? 0,
                xml: data.xml ?? "",
                report: data.report ?? { errors: [], warnings: [], manualReview: [] },
            });
            setBuildWarnings(data.report?.warnings ?? []);
            setXmlModalOpen(true);
            // "File" delivers the batch — force the download as soon as the build succeeds
            // (Austin/Zach, 2026-06-25: "force the download if done"). The modal stays open as a
            // re-download + report view.
            downloadXmlFile(data.xml ?? "", data.fileName ?? "filing.xml");
        } catch (err: any) {
            Swal.fire({
                title: "Build Failed",
                text: err?.message || "Network error while building batch",
                icon: "error",
                confirmButtonColor: BLUE,
            });
        } finally {
            setBuildLoading(false);
        }
    }, [customerPolicyId, token, overrides, FILING_BASE, surfaceBuildErrors]);

    // ---------------------------------------------------------------------------
    // Download XML
    // ---------------------------------------------------------------------------
    const handleDownload = useCallback(() => {
        if (!buildResult?.xml) return;
        downloadXmlFile(buildResult.xml, buildResult.fileName);
    }, [buildResult]);

    // ---------------------------------------------------------------------------
    // Render helpers
    // ---------------------------------------------------------------------------
    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 300,
                    gap: 2,
                }}
            >
                <CircularProgress sx={{ color: BLUE }} />
                <Typography sx={{ color: SLATE_400, fontSize: "14px" }}>
                    Loading filing data…
                </Typography>
            </Box>
        );
    }

    if (fetchError) {
        return (
            <Box sx={{ p: 3 }}>
                <Button startIcon={<ArrowBack />}
                    onClick={() => navigate(AppRoutes.filingQueue.batchReview)}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        color: BLUE,
                        mb: 2,
                    }}
                >Back to Batch Filing Review</Button>
                <Alert severity="error" sx={{ fontSize: "14px" }}>
                    Failed to load filing review: {fetchError}
                </Alert>
            </Box>
        );
    }

    if (!reviewData) return null;

    const grouped = groupFields(reviewData.fields ?? []);
    // The backend nests readiness under report.readiness; the documented contract also exposes it
    // top-level. Read either so the panel renders regardless of which the API sends.
    const readiness = reviewData.readiness ?? reviewData.report?.readiness;
    const gapKeys = new Set((readiness?.gaps ?? []).map((g) => g.key));
    const report = reviewData.report ?? {
        errors: [],
        warnings: [],
        manualReview: [],
    };

    const lloydsMRFlags = report.manualReview.filter(
        (f) => f.kind === "lloyds_syndicate_confirm"
    );

    // The dec-page "Writing insurer NAIC" override is only meaningful when the resolved Insurer NAIC is
    // absent from the record, or it's a Lloyd's syndicate (generic NAIC resolved → the reviewer must enter
    // the specific syndicate's NAIC-filed number). For a clean carrier whose NAIC is on record (e.g.
    // Intrepid 17181, shown under "Insurer NAIC"), the empty override field just reads as a missing NAIC —
    // so hide it unless it is actually needed.
    const resolvedInsurerNaic = (reviewData.fields ?? []).find((f) => f.key === "insurerNaic")?.value;
    const showWritingInsurerNaicDoc =
        lloydsMRFlags.length > 0 || resolvedInsurerNaic == null || resolvedInsurerNaic === "";
    // UMR is a Lloyd's-only field — hide it for every other carrier (Kelly, 2026-07-14).
    const isLloyds = isLloydsInsurer(reviewData.fields ?? [], lloydsMRFlags.length > 0);
    const otherMRFlags = report.manualReview.filter(
        (f) => f.kind !== "lloyds_syndicate_confirm"
    );

    const hasChanges = Object.keys(overrides).length > 0;
    // A multi-transaction policy must resolve to one OpenTaxReportId before building (see handleBuild).
    // The engine can reject a filing for reasons that are not field gaps (e.g. an unresolved coverage
    // code). "Clear to file" requires BOTH no readiness gaps AND no report errors, and respects the
    // backend's own `ready` flag — so the panel never shows green on a policy the engine won't file.
    //
    // Whether a manual-review flag blocks is the BACKEND's call and varies by state (reviewModel
    // buildReview / an adapter's manualReviewBlocks), so `ready` already accounts for it — TN/TX flags
    // still force ready=false, while FL's advisory ones do not. This must NOT re-apply that rule on top:
    // duplicating it here overrode the backend and stranded a policy at "Not ready to file" for a flag the
    // state deems informational, with nothing for the user to click (flags are re-derived per request and
    // no acknowledgement is recorded). The flags still render below either way.
    const hasBlockers = report.errors.length > 0;
    const clearToFile =
        (readiness ? readiness.gaps.length === 0 : true) &&
        !hasBlockers &&
        reviewData.ready !== false;
    // Reasons a policy is NOT clear to file, summarized for the verdict meter. These map 1:1 to the
    // detail sections below — missing required fields → "Areas to fix"; blockers → "Filing blockers";
    // needs-review → the manual-review alerts — and are kept as separate counts so nothing is double-
    // counted. This is why a policy can be 100% on required fields yet still not be clear to file.
    const gapCount = readiness?.gaps?.length ?? 0;
    const blockerSummary = [
        gapCount ? `${gapCount} required field${gapCount !== 1 ? "s" : ""} missing` : null,
        report.errors.length ? `${report.errors.length} blocker${report.errors.length !== 1 ? "s" : ""}` : null,
        report.manualReview.length ? `${report.manualReview.length} need${report.manualReview.length === 1 ? "s" : ""} review` : null,
    ].filter(Boolean).join(" · ");
    // Advisory flags on a policy that IS clear to file (a state where manual review doesn't gate `ready`).
    // Same wording as the blocker summary's review clause, so the verdict reads consistently either way.
    const reviewNoteSummary = report.manualReview.length
        ? `${report.manualReview.length} need${report.manualReview.length === 1 ? "s" : ""} review`
        : "";

    return (
        <Box sx={{ px: { xs: 2, md: 3 }, py: 2.5, maxWidth: 1300, mx: "auto" }}>
            {/* ---- Top bar ---- */}
            <Box sx={{ position: "fixed", top: 0, left: 0, zIndex: 9990, width: "100%" }}>
                {buildLoading && (
                    <LinearProgress
                        sx={{ backgroundColor: DARK_SIDEBAR, "& .MuiLinearProgress-bar": { backgroundColor: BLUE } }}
                    />
                )}
            </Box>

            {/* ---- Back link ---- */}
            <Button startIcon={<ArrowBack />}
                onClick={() => navigate(AppRoutes.filingQueue.batchReview)}
                sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    color: BLUE,
                    mb: 1.5,
                    pl: 0,
                    "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
                }}
            >Back to Batch Filing Review</Button>

            {/* ---- Page title ---- */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                    pb: 1.75,
                    borderBottom: `1px solid ${BORDER}`,
                    flexWrap: "wrap",
                }}
            >
                <Typography sx={{ fontSize: "20px", fontWeight: 700, color: SLATE_700 }}>
                    {segment} Filing Review
                </Typography>
                <Chip
                    label={segment}
                    size="small"
                    sx={{
                        backgroundColor: "#f6f8fa",
                        border: `1px solid #cdd5db`,
                        color: SLATE_500,
                        fontWeight: 600,
                        fontSize: "12.5px",
                    }}
                />
                <Button variant="contained" size="medium"
                    onClick={() => navigate(`/Admin/AddEditCustomerPolicyDetails/${customerPolicyId}`)}
                    title={`View policy details for Policy ID: ${customerPolicyId}`}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        backgroundColor: BLUE,
                        "&:hover": { backgroundColor: BLUE_HOVER },
                    }}
                >View Policy Details</Button>
                {reviewData?.extractedAt && (
                    <Typography
                        title="When the latest extraction for this policy ran"
                        sx={{ ml: "auto", fontSize: "12.5px", color: SLATE_400, whiteSpace: "nowrap" }}
                    >
                        Last extracted {formatDateTime(reviewData.extractedAt)}
                    </Typography>
                )}
            </Box>

            {/* Transaction switcher removed (2026-06-25): filing is one-per-CustomerPolicy and the
                OpenTaxReports tax + fee ledger lines collapse to a single transaction, so there is
                nothing to switch between. A genuine renewal/endorsement files the latest transaction. */}

            {/* ---- Readiness panel ---- */}
            {readiness && (
                <Card
                    sx={{
                        mb: 2.5,
                        borderRadius: "12px",
                        boxShadow: CARD_SHADOW,
                        border: `1px solid ${BORDER}`,
                    }}
                >
                    <CardContent
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 3,
                            flexWrap: "wrap",
                            py: "14px !important",
                            px: 2.5,
                        }}
                    >
                        {/* ── Meter 1: filing-readiness VERDICT — the real "can this file?" answer.
                              Driven by clearToFile (required fields + no blockers + the backend's own
                              `ready`, which is where manual-review flags do or don't count per state),
                              NOT by the required-fields %. ── */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 210 }}>
                            {clearToFile ? (
                                <CheckCircleOutline sx={{ color: "#2e7d32", fontSize: 26, flexShrink: 0 }} />
                            ) : hasBlockers ? (
                                <ErrorOutline sx={{ color: "#c0392b", fontSize: 26, flexShrink: 0 }} />
                            ) : (
                                <WarningAmberOutlined sx={{ color: "#b26a00", fontSize: 26, flexShrink: 0 }} />
                            )}
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                                <Typography
                                    sx={{
                                        fontSize: "18px",
                                        fontWeight: 800,
                                        lineHeight: 1.1,
                                        color: clearToFile ? "#2e7d32" : hasBlockers ? "#c0392b" : "#b26a00",
                                    }}
                                >
                                    {clearToFile ? "Clear to file" : "Not ready to file"}
                                </Typography>
                                <Typography sx={{ fontSize: "12px", color: SLATE_400 }}>
                                    {/* A policy can now be clear to file WITH advisory flags (a state whose
                                        manual-review items don't gate `ready` — e.g. FL, where an off-list
                                        insurer is accepted-and-auto-questioned, not a blocker). Saying "All
                                        checks passed" next to a visible needs-review chip would contradict
                                        it, so name the flags in the sub-line instead. */}
                                    {clearToFile
                                        ? (reviewNoteSummary ? `Clear to file · ${reviewNoteSummary}` : "All checks passed")
                                        : (blockerSummary || "Not ready")}
                                </Typography>
                            </Box>
                        </Box>

                        <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />

                        {/* ── Meter 2: required-fields COMPLETENESS — data only, NOT the verdict.
                              Counts whether each required field has a value (document or database); a
                              full bar does not by itself mean the policy can file. ── */}
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 0.5 }}>
                                <Typography
                                    sx={{
                                        fontSize: "10.5px",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.04em",
                                        color: SLATE_400,
                                        fontWeight: 700,
                                    }}
                                >
                                    Required Fields
                                </Typography>
                                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: SLATE_700, fontVariantNumeric: "tabular-nums" }}>
                                    {readiness.requiredReady} / {readiness.requiredTotal}
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={readiness.grade * 100}
                                sx={{
                                    height: 8,
                                    borderRadius: 999,
                                    backgroundColor: "#eef2f5",
                                    "& .MuiLinearProgress-bar": {
                                        background: readiness.requiredReady >= readiness.requiredTotal
                                            ? "linear-gradient(90deg,#2e9e5b,#39b56a)"
                                            : "linear-gradient(90deg,#708999,#8aa1b0)",
                                        borderRadius: 999,
                                    },
                                }}
                            />
                            <Typography sx={{ fontSize: "12px", color: SLATE_400, mt: 0.5 }}>
                                {readiness.requiredReady} of {readiness.requiredTotal} required fields have a value (document or database) — completeness only, not a guarantee the filing will pass.
                            </Typography>
                        </Box>
                    </CardContent>

                    {/* Gaps list */}
                    {readiness.gaps.length > 0 && (
                        <>
                            <Divider />
                            <Box sx={{ px: 2.5, py: 1.5, backgroundColor: "#fffaf2" }}>
                                <Typography
                                    sx={{
                                        fontSize: "12px",
                                        fontWeight: 700,
                                        color: "#b26a00",
                                        mb: 1,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.04em",
                                    }}
                                >
                                    Areas to fix
                                </Typography>
                                <Stack spacing={0.75}>
                                    {readiness.gaps.map((gap) => (
                                        <Box
                                            key={gap.key}
                                            sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                                        >
                                            <ErrorOutline
                                                sx={{ color: "#b26a00", fontSize: 16, mt: "2px", flexShrink: 0 }}
                                            />
                                            <Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: "13.5px",
                                                        fontWeight: 600,
                                                        color: SLATE_700,
                                                    }}
                                                >
                                                    {gap.label}
                                                </Typography>
                                                {gap.note && (
                                                    <Typography sx={{ fontSize: "12px", color: SLATE_400 }}>
                                                        {gap.note}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        </>
                    )}
                </Card>
            )}

            {/* ---- Manual review flags ---- */}
            {lloydsMRFlags.length > 0 && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2, borderRadius: "10px", fontSize: "14px" }}
                    icon={<WarningAmberOutlined fontSize="inherit" />}
                >
                    <Typography sx={{ fontWeight: 700, fontSize: "14px", mb: 0.5 }}>
                        Lloyd's Syndicate NAIC confirmation required
                    </Typography>
                    {lloydsMRFlags.map((f, i) => (
                        <Typography key={i} sx={{ fontSize: "13px" }}>
                            {f.reason} — enter the syndicate-specific NAIC in the{" "}
                            <strong>Writing Insurer NAIC (document override)</strong> field below.
                        </Typography>
                    ))}
                </Alert>
            )}

            {otherMRFlags.map((f, i) => (
                <Alert key={i} severity="info" sx={{ mb: 1.5, borderRadius: "10px", fontSize: "13.5px" }}>
                    <strong>{kindLabel(f.kind)}</strong>
                    {f.reason ? ` — ${f.reason}` : ""}
                </Alert>
            ))}

            {/* ---- Filing blockers from the loaded review (engine-level rejections that aren't field
              gaps — e.g. an unresolved coverage code). Surfaced on load, not just after a build,
              so a not-ready policy never looks green. ---- */}
            {hasBlockers && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }} icon={<ErrorOutline fontSize="inherit" />}>
                    <Typography sx={{ fontWeight: 700, fontSize: "14px", mb: 0.5 }}>
                        Filing blockers — must be resolved before this can be filed
                    </Typography>
                    {report.errors.map((e, i) => (
                        <Typography key={i} sx={{ fontSize: "13px" }}>
                            {e}
                        </Typography>
                    ))}
                </Alert>
            )}
            {report.warnings.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1.5, borderRadius: "10px", fontSize: "13px" }}>
                    {report.warnings.map((w, i) => (
                        <div key={i}>{w}</div>
                    ))}
                </Alert>
            )}

            {/* ---- Report errors/warnings from last build ---- */}
            {buildErrors.length > 0 && (
                <Alert ref={buildErrorsRef} severity="error" sx={{ mb: 2, borderRadius: "10px" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "14px", mb: 0.5 }}>
                        Build failed
                    </Typography>
                    {buildErrors.map((e, i) => (
                        <Typography key={i} sx={{ fontSize: "13px" }}>
                            {e}
                        </Typography>
                    ))}
                </Alert>
            )}
            {buildWarnings.length > 0 && buildErrors.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: "10px" }}>
                    {buildWarnings.map((w, i) => (
                        <Typography key={i} sx={{ fontSize: "13px" }}>
                            {w}
                        </Typography>
                    ))}
                </Alert>
            )}

            {/* ---- Field group cards ---- */}
            {GROUP_ORDER.map((group) => {
                // Only the fields this state actually files survive (checklist #11 trimming + Lloyd's-only
                // UMR); if none do, skip the whole card so an empty section never renders.
                const visibleFields = (grouped[group] ?? [])
                    .filter((f) => f.key !== "writingInsurerNaicDoc" || showWritingInsurerNaicDoc)
                    .filter((f) => f.key !== "umr" || isLloyds)
                    // Hide the legacy combined address line — the structured components are the edit surface.
                    .filter((f) => !LEGACY_ADDRESS_KEYS.has(f.key))
                    // Keep the structured address components even though the backend marks them required:false.
                    .filter((f) => f.required !== false || ADDRESS_COMPONENT_KEYS.has(f.key));
                if (visibleFields.length === 0) return null;
                const isEditable = group === "document";

                return (
                    <Card key={group}
                        sx={{
                            mb: 2.5,
                            borderRadius: "12px",
                            boxShadow: CARD_SHADOW,
                            border: `1px solid ${BORDER}`,
                            overflow: "hidden",
                        }}
                    >
                        <SectionHeader title={GROUP_LABELS[group] || group}
                            hint={isEditable
                                ? "Values extracted from policy documents — edit to correct before building XML"
                                : "Read-only · sourced from policy record or system config"
                            }
                        />
                        <CardContent sx={{ p: 2.5 }}>
                            <Grid container spacing={2.5}>
                                {visibleFields.map((f) => {
                                        // UMR reads as its plain-language Lloyd's name on this screen too.
                                        const df = f.key === "umr" ? { ...f, label: LLOYDS_UMR_LABEL } : f;
                                        return (
                                            <Grid item key={f.key} xs={12} sm={6}
                                                md={
                                                    f.key === "descriptionOfRisk" ||
                                                        f.key === "physicalRiskAddress" ||
                                                        f.key === "mailingAddress" ||
                                                        f.key === "coverageLimits"
                                                        ? 8
                                                        : 4
                                                }
                                            >
                                                {f.editable ? (
                                                    <EditableField field={df}
                                                        value={editValues[f.key] ?? ""}
                                                        onChange={handleFieldChange}
                                                        isLloydsNaicField={
                                                            f.key === "writingInsurerNaicDoc" &&
                                                            lloydsMRFlags.length > 0
                                                        }
                                                    />
                                                ) : (
                                                    <ReadOnlyField field={df} isGap={gapKeys.has(f.key)} />
                                                )}
                                                <FieldProvenance field={df} docUrl={reviewData.sourceDocumentUrl}
                                                    onViewDocument={(url, name, page) => {
                                                        setDocViewerDocuments([{ documentUrl: url, documentName: name, page: page ?? null }]);
                                                        setDocViewerOpen(true);
                                                    }}
                                                />
                                            </Grid>
                                        );
                                    })}
                            </Grid>
                        </CardContent>
                    </Card>
                );
            })}

            {/* ---- Sticky action bar ---- */}
            <Box
                sx={{
                    position: "sticky",
                    bottom: 0,
                    zIndex: 100,
                    backgroundColor: "#fff",
                    border: `1px solid ${BORDER}`,
                    borderRadius: "10px",
                    px: 2.5,
                    py: 1.75,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    boxShadow: "0 -2px 16px rgba(20,40,60,0.06)",
                    flexWrap: "wrap",
                    mt: 1,
                }}
            >
                <Box>
                    {readiness ? (
                        clearToFile ? (
                            <Typography sx={{ fontSize: "13.5px", color: SLATE_400 }}>
                                <CheckCircleOutline
                                    sx={{ color: "#2e7d32", fontSize: 16, verticalAlign: "text-bottom", mr: 0.5 }}
                                />
                                <span style={{ color: "#2e7d32", fontWeight: 700 }}>
                                    Readiness: {pct(readiness.grade)}
                                </span>{" "}
                                — clear to file
                            </Typography>
                        ) : hasBlockers ? (
                            <Typography sx={{ fontSize: "13.5px", color: SLATE_400 }}>
                                <ErrorOutline
                                    sx={{ color: "#c0392b", fontSize: 16, verticalAlign: "text-bottom", mr: 0.5 }}
                                />
                                <span style={{ color: "#c0392b", fontWeight: 700 }}>
                                    {report.errors.length} filing blocker{report.errors.length !== 1 ? "s" : ""}
                                </span>{" "}
                                — resolve before filing (see above)
                            </Typography>
                        ) : (
                            <Typography sx={{ fontSize: "13.5px", color: SLATE_400 }}>
                                <WarningAmberOutlined
                                    sx={{ color: "#b26a00", fontSize: 16, verticalAlign: "text-bottom", mr: 0.5 }}
                                />
                                <span style={{ color: "#b26a00", fontWeight: 700 }}>
                                    {readiness.gaps.length} gap{readiness.gaps.length !== 1 ? "s" : ""} remaining
                                </span>{" "}
                                — you can still build; errors will appear in the report
                            </Typography>
                        )
                    ) : (
                        <Typography sx={{ fontSize: "13.5px", color: SLATE_400 }}>
                            Review fields above, then build XML
                        </Typography>
                    )}
                    {hasChanges && (
                        <Typography sx={{ fontSize: "12px", color: "#0d4a63", mt: 0.25 }}>
                            {Object.keys(overrides).length} field override
                            {Object.keys(overrides).length !== 1 ? "s" : ""} staged
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        size="medium"
                        onClick={fetchReview}
                        disabled={loading || buildLoading || extractLoading}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderColor: BORDER,
                            color: SLATE_500,
                            "&:hover": { borderColor: BLUE, color: BLUE },
                        }}
                    >
                        Reload
                    </Button>
                    <Button
                        variant="outlined"
                        size="medium"
                        onClick={handleExtract}
                        disabled={loading || buildLoading || extractLoading}
                        title="Run the extraction engine on this policy's document(s) and refresh the review"
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderColor: BORDER,
                            color: SLATE_500,
                            "&:hover": { borderColor: BLUE, color: BLUE },
                        }}
                    >
                        {extractLoading ? "Extracting…" : "Re-extract"}
                    </Button>
                    <Button variant="outlined"
                        size="medium"
                        onClick={handleViewPolicyDocument}
                        disabled={loading || buildLoading || extractLoading || !reviewData?.sourceDocumentUrl}
                        title={reviewData?.sourceDocumentUrl ? "View the policy document" : "No source document available"}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderColor: BORDER,
                            color: SLATE_500,
                            "&:hover": { borderColor: BLUE, color: BLUE },
                        }}
                    >View Policy Document</Button>
                    <Button
                        variant="contained"
                        size="medium"
                        startIcon={<Build />}
                        onClick={handleBuild}
                        disabled={hasBlockers || buildErrors.length > 0 || buildLoading}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            backgroundColor: GreenBtnColor,
                            "&:hover": { backgroundColor: buttonColors.success },
                            "&:disabled": { opacity: 0.6 },
                        }}
                    >
                        {buildLoading ? "Filing…" : "File"}
                    </Button>
                </Box>
            </Box>

            {/* ---- Policy document viewer ---- */}
            <DocumentViewer open={docViewerOpen} documents={docViewerDocuments} onClose={handleCloseDocViewer} />

            {/* ---- XML Preview Modal ---- */}
            <Dialog
                open={xmlModalOpen}
                onClose={() => setXmlModalOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "12px",
                        boxShadow: "0 30px 60px rgba(0,0,0,0.25)",
                    },
                }}
            >
                <DialogTitle
                    sx={{ fontWeight: 700, fontSize: "18px", color: SLATE_700, pb: 0.5 }}
                >
                    SLIP+ XML Preview
                </DialogTitle>
                <DialogContent>
                    {buildResult && (
                        <Typography sx={{ fontSize: "13.5px", color: SLATE_400, mb: 1.5 }}>
                            {buildResult.fileName}
                            {buildResult.sizeBytes > 0
                                ? ` · ${(buildResult.sizeBytes / 1024).toFixed(1)} KB`
                                : ""}
                        </Typography>
                    )}

                    {buildWarnings.length > 0 && (
                        <Alert severity="warning" sx={{ mb: 1.5, fontSize: "13px" }}>
                            {buildWarnings.map((w, i) => (
                                <div key={i}>{w}</div>
                            ))}
                        </Alert>
                    )}

                    <Box
                        component="pre"
                        sx={{
                            backgroundColor: "#0f1f2b",
                            color: "#cbd5e1",
                            borderRadius: "10px",
                            p: 2,
                            fontFamily: "ui-monospace, Consolas, monospace",
                            fontSize: "11px",
                            lineHeight: 1.5,
                            maxHeight: 380,
                            overflowY: "auto",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-all",
                            m: 0,
                        }}
                    >
                        {buildResult?.xml ?? ""}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setXmlModalOpen(false)}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderColor: BORDER,
                            color: SLATE_500,
                        }}
                    >
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Download />}
                        onClick={handleDownload}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            backgroundColor: BLUE,
                            "&:hover": { backgroundColor: BLUE_HOVER },
                        }}
                    >
                        Download XML
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
export default FilingReview;